import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Observable, Subscribable } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import IdeationManager from 'managers/ideation';
import { IWCCStorageManager } from 'managers/iStorage';
import CommentRepliesManager from 'managers/topic/comment/replies';
import { TopicThread } from 'models/topicThread';
import TextThreadEditorVM from './textThread';
import './whisperButton.html';
import './whisperButton.scss';

components.preload('wcc-list');

interface WhisperToPerson {
    fullName: Subscribable<string>
    imageURL: Subscribable<string>
    personId: Subscribable<string | undefined>
    isSelected: Observable<boolean>
}

interface TextThreadEditorWhisperButtonVMConfig {
    editor: TextThreadEditorVM
}

@injectable()
export default class TextThreadEditorWhisperButtonVM {
    private editor: TextThreadEditorVM
    private parentThread: Subscribable<TopicThread | undefined>

    private allowToWhisper: Subscribable<boolean>

    labels = labels

    userId: Subscribable<string | undefined>
    userName: Subscribable<string>

    whisperList: Subscribable<Array<WhisperToPerson>>
    hasPeopleToWhisper = ko.pureComputed(() => this.whisperList().length > 0)    

    allowToSave = ko.pureComputed(() => this.editor.allowToSave())

    constructor(
        @inject(wccModules.componentConfig) { editor }: TextThreadEditorWhisperButtonVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager
    ) {
        this.editor = editor;

        const { threadOwnerId, topicId, topic, parentThread } = editor;

        this.parentThread = parentThread;
        const parentThreadId = parentThread.pluck('id');
        const parentThreadRepliesCount = parentThread.pluck('repliesCount', 0);
        const parentThreadHasNoReplies = ko.pureComputed(() => parentThreadRepliesCount() === 0);

        const userManager = storage.get(CurrentUserManager, { discussionId: settings.discussionId });
        const userReady = userManager.pluck(m => !m.loading(), false);
        const user = userManager.pluck(m => m.person);
        this.userId = user.pluck(u => u.personId);
        this.userName = user.pluck(u => u.nicknameOrName, '');
        const isModerator = user.pluck(u => u.isModerator, false);

        const repliesManager = storage.get(CommentRepliesManager, { topicId, commentId: parentThreadId, isEmpty: parentThreadHasNoReplies, view: 'all' });
        const isLoadingReplies = repliesManager.pluck('loading', true);
        const allReplies = repliesManager.pluck(m => m.replies, []);

        const ideationManager = storage.get(IdeationManager, { topicId });
        const ideationReady = ideationManager.pluck(m => m.isReady, false);
        const isIdeation = ideationManager.pluck(m => m.isIdeation, false);
        const isRankingStage = ideationManager.pluck(m => m.isRatingStage, false);

        const isReady = ko.pureComputed(() => {
            return isModerator() &&
                editor.isReady() &&
                userReady() &&
                ideationReady() &&
                (parentThread() == undefined || !isLoadingReplies());
        });

        this.allowToWhisper = ko.pureComputed(() => {
            const currentTopic = topic();

            if (currentTopic != undefined) {
                return isReady() &&
                    isModerator() &&
                    this.userId() == threadOwnerId() &&
                    this.parentThread() != undefined &&
                    (!isIdeation() || isRankingStage());
            }

            return false;
        });

        this.whisperList = ko.pureComputed(() => {
            const parentThread = this.parentThread();

            if (parentThread == undefined)
                return [];

            return [parentThread].concat(allReplies()).reduce((list, thread) => {
                const isValid = !thread.isModerator() &&
                    !thread.isObserver() &&
                    !list.some(item => item.personId() === thread.postedById());

                if (isValid)
                    list.push({
                        fullName: thread.creatorName,
                        imageURL: thread.creatorImageUrl,
                        personId: thread.postedById,
                        isSelected: ko.observable(thread.postedById() === parentThread.visibleToId())
                    });

                return list;
            }, [] as Array<WhisperToPerson>);
        });

        ko.applyBindingsToNode(info.element, {
            'visible': this.allowToWhisper,

            'css': {
                'thread-editor-text-whisper-button': true
            }
        }, this);
    }

    saveWhisper = () => {
        if (this.allowToWhisper()) {
            this.editor.saveAdvanced({
                Visibility: enums.ThreadVisibility.Private.value
            });
        }
    }

    saveDirectWhisper = (person: WhisperToPerson) => {
        if (this.allowToWhisper()) {
            this.editor.saveAdvanced({
                Visibility: enums.ThreadVisibility.Private.value,
                VisibleToId: person.personId(),
                VisibleToFullName: person.fullName()
            });
        }
    }
}